import { useState } from 'react'
import { Accordion, Button, ContactBlock, EmptyState, RequestCard, Skeleton } from '@/ds'
import { LinkRow } from '@/app/components/LinkRow'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { telHref } from '@/lib/format'
import { whatsappHref } from '@/lib/whatsapp'
import { useAppNavigate } from '@/lib/query-params'
import { hasMarkup, richText } from '@/lib/richText'
import { plainText } from '@/lib/text'
import '../Guia/Guia.css'
import { asyncValue, useAsync } from '@/lib/useAsync'
import { featureEnabled } from '@/config/features'
import { ayudaService } from '@/services/ayuda'
import { peticionesService } from '@/services/peticiones'
import { SectionError } from '../Estancia/sections/SectionError'
import { useLang } from '../Reserva/shared'
import { contactOf, HELP_ROUTES, requestMeta, requestTitle, updatedText } from './shared'

/**
 * 8.9 · Ayuda (PantallaAyuda.dc.html, Bloque 6 §6h): nueva petición, tus peticiones, preguntas frecuentes, la
 * conversación en solo lectura y contacto. Sin chat propio: WhatsApp con la referencia, email y teléfono como
 * alternativa y el teléfono de emergencia siempre visible, también sin conexión (D51). Peticiones, preguntas y
 * conversación solo con mock; con la API actual se ocultan y queda el contacto.
 */
export function Ayuda() {
  const { t, lang } = useLang()
  const stay = useLoadedStay()
  const { go } = useAppNavigate()
  const [now] = useState(() => new Date())
  const showRequests = featureEnabled('ayudaPeticiones')
  const showFaq = featureEnabled('ayudaFaq')
  const showConversation = featureEnabled('ayudaConversacion')
  const [requests, reloadRequests] = useAsync(`peticiones:${stay.guestCode}`, (signal) => (showRequests ? peticionesService.list(stay, signal) : Promise.resolve([])))
  const [faq, reloadFaq] = useAsync(`ayuda-faq:${stay.guestCode}`, (signal) => (showFaq ? ayudaService.getFaq(stay, signal) : Promise.resolve([])))
  const [conversation] = useAsync(`ayuda-conversacion:${stay.guestCode}`, (signal) => (showConversation ? ayudaService.getConversation(stay, signal) : Promise.resolve([])))
  const [contactData] = useAsync(`ayuda-contacto:${stay.guestCode}`, (signal) => ayudaService.getContact(stay, signal))
  // El contacto de la reserva (guardada con ella) mientras carga o si falla: emergencias siempre visible.
  const contact = asyncValue(contactData) ?? contactOf(stay)
  const withRequests = showRequests && requests.kind !== 'unsupported'
  const list = [...(asyncValue(requests) ?? [])].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  const faqItems = asyncValue(faq) ?? []

  return (
    <main className="page">
      <div className="pagos__head">
        <h1>{t('v2.ayuda.titulo')}</h1>
        <p className="page__lead">{withRequests ? t('v2.ayuda.lead') : t('v2.ayuda.leadReal')}</p>
      </div>

      {withRequests && (
        <>
          <Button icon="plus" onClick={() => go(HELP_ROUTES.newRequest)}>
            {t('v2.ayuda.nueva')}
          </Button>
          <section className="pagos__group" aria-labelledby="ayuda-peticiones">
            <h2 id="ayuda-peticiones" className="pagos__h2">
              {t('v2.peticiones.lista.titulo')}
            </h2>
            {requests.kind === 'loading' && (
              <div aria-busy="true">
                <Skeleton height={88} />
              </div>
            )}
            {requests.kind === 'error' && <SectionError code={requests.code} onRetry={reloadRequests} />}
            {requests.kind === 'ready' && list.length === 0 && (
              <EmptyState icon="message-circle" title={t('v2.peticiones.lista.vacioTitulo')} headingLevel={3}>
                {t('v2.peticiones.lista.vacioTexto')}
              </EmptyState>
            )}
            {list.length > 0 && (
              <ul className="ayuda__list">
                {list.map((r) => (
                  <li key={r.id}>
                    <RequestCard title={requestTitle(r, t)} status={r.status} date={requestMeta(r, now, lang, t)} updated={updatedText(r.updatedAt, now, lang, t)} onClick={() => go(HELP_ROUTES.request(r.id))} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      {showFaq && faq.kind !== 'unsupported' && (faq.kind !== 'ready' || faqItems.length > 0) && (
        <section className="pagos__group" aria-labelledby="ayuda-faq">
          <h2 id="ayuda-faq" className="pagos__h2">
            {t('v2.ayuda.faq')}
          </h2>
          {faq.kind === 'loading' && (
            <div aria-busy="true">
              <Skeleton height={56 * 3} />
            </div>
          )}
          {faq.kind === 'error' && <SectionError code={faq.code} onRetry={reloadFaq} />}
          {faqItems.length > 0 && <Accordion items={faqItems.map((f) => ({ id: f.id, title: f.question, content: hasMarkup(f.answer) ? <div className="guia__rich">{richText(f.answer)}</div> : f.answer, searchText: plainText(f.answer) }))} className="ayuda__faq" />}
        </section>
      )}

      {showConversation && conversation.kind !== 'unsupported' && conversation.kind !== 'loading' && <LinkRow icon="message-circle" title={t('v2.ayuda.conversacionFila')} subtitle={t('v2.ayuda.conversacionFilaSub')} to={HELP_ROUTES.conversation} />}

      <section className="pagos__group" aria-labelledby="ayuda-contacto">
        <h2 id="ayuda-contacto" className="pagos__h2">
          {t('v2.ayuda.contacto')}
        </h2>
        {contact.whatsapp || contact.email || contact.phone || contact.emergencyPhone ? (
          <ContactBlock
            {...(contact.whatsapp ? { whatsappHref: whatsappHref(contact.whatsapp, contact.reference), whatsappNote: t('v2.ayuda.whatsappNota', { reference: contact.reference }) } : {})}
            {...(contact.email ? { emailHref: `mailto:${contact.email}` } : {})}
            {...(contact.phone ? { phoneHref: telHref(contact.phone) } : {})}
            emergencyPhone={contact.emergencyPhone}
          />
        ) : (
          <p className="pagos__note">{t('v2.ayuda.sinContacto')}</p>
        )}
      </section>
    </main>
  )
}
